/**
 * MessagingAdapter on a real outbox: `send` writes a `queued` row to `messages`; the scheduler Edge Function (pg_cron, every minute)
 * delivers due rows through WhatsApp / SMS after re-checking consent, quiet hours and the plan allowance server side; Meta's status
 * callbacks update the rows. The browser never delivers anything, so `tick()` does nothing there. On the server a `dispatcher` can be
 * injected so interactive templates (deposit links, closure notices) go out in the same request (`dispatchNow`).
 */
import { newId, type Id, type Locale, type Message, type SuppressionReason } from '../../domain/types';
import { toMs } from '../../domain/time';
import type { Clock, MessageRecord, MessagingAdapter, OutboxFilter, SendRequest, StorageAdapter, Unsubscribe } from '../types';
import { renderTemplate } from '../messaging/render';
import { assertSafeParams } from '../messaging/templates';

export interface OutboxMessagingDeps {
  storage: StorageAdapter;
  clock: Clock;
  locale?: () => Locale;
  /** Server only: delivers one due message now and returns the updated row. */
  dispatcher?: (id: Id) => Promise<MessageRecord | undefined>;
  /** Server only: delivers every due queued message (scheduler). Returns how many were handled. */
  dispatchDue?: () => Promise<number>;
}

export function createOutboxMessaging(deps: OutboxMessagingDeps): MessagingAdapter {
  const { storage, clock, dispatcher } = deps;
  const repo = storage.repo('messages');
  const locale = deps.locale ?? ((): Locale => 'he');

  async function create(req: SendRequest, status: Message['status'], reason?: SuppressionReason): Promise<MessageRecord> {
    assertSafeParams(req.call.template, req.call.params);
    const [dup] = await repo.list({ where: { businessId: req.businessId, dedupeKey: req.dedupeKey }, filter: (m) => m.status !== 'cancelled' });
    if (dup) return dup;
    const now = clock.nowIso();
    const row: Message = {
      id: newId(clock.now()), businessId: req.businessId, createdAt: now, updatedAt: now,
      channel: 'whatsapp', template: req.call.template, to: req.to, params: { ...req.call.params }, status, scheduledFor: req.scheduledFor ?? now,
      ...(reason ? { suppressionReason: reason } : req.deferred ? { suppressionReason: 'quiet_hours_deferred' as const } : {}),
      ...(req.appointmentId ? { appointmentId: req.appointmentId } : {}),
      clientId: req.clientId, purpose: req.purpose, dedupeKey: req.dedupeKey, text: renderTemplate(req.call, locale()), mock: true,
    };
    return repo.put(row);
  }
  async function patch(id: Id, change: Partial<Message>): Promise<void> {
    const cur = await repo.get(id);
    if (cur) await repo.put({ ...cur, ...change, updatedAt: clock.nowIso() });
  }

  const adapter: MessagingAdapter = {
    label: 'WhatsApp Business (Meta Cloud API)',
    send: (req) => create(req, 'queued'),
    suppress: (req, reason) => create(req, 'suppressed', reason),
    async cancel(id) {
      const cur = await repo.get(id);
      if (cur && (cur.status === 'queued' || (cur.status === 'suppressed' && cur.suppressionReason === 'plan_free_manual'))) await patch(id, { status: 'cancelled' });
    },
    async reschedule(id, scheduledFor, reason) { await patch(id, { scheduledFor, ...(reason ? { suppressionReason: reason } : {}) }); },
    async markSuppressed(id, reason) { await patch(id, { status: 'suppressed', suppressionReason: reason }); },
    async markManualSent(id) { await patch(id, { status: 'sent', sentAt: clock.nowIso(), manual: true }); },
    async listOutbox(filter: OutboxFilter = {}) {
      const { businessId, ...rest } = filter;
      const rows = await repo.list({ where: { ...(businessId ? { businessId } : {}), ...rest } });
      return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
    },
    onStatus(cb): Unsubscribe {
      return storage.subscribe('messages', (e) => {
        if (e.type === 'delete') return;
        if (e.id) void repo.get(e.id).then((m) => { if (m) cb(m); });
      });
    },
    tick: async () => (deps.dispatchDue ? deps.dispatchDue() : 0),
  };
  if (dispatcher) {
    adapter.dispatchNow = async (id) => {
      const cur = await repo.get(id);
      if (!cur || cur.status !== 'queued' || toMs(cur.scheduledFor) > clock.now()) return cur;
      return dispatcher(id);
    };
  }
  return adapter;
}
